import type { GetContextMenuItemsParams } from 'ag-grid-community';
import Big from 'big.js';
import { compact, every } from 'lodash-es';

import { useDetailsDrawer } from '@src/components/DetailsDrawer/useDetailsDrawer';
import { orderSliceActions } from '@src/components/OMS/NewOrder/OrderSlice';
import {
  getOrderFormEligibilityForResubmit,
  getOrderFormEligibilityForSymbol,
  resubmitsViaSalesOrderForm,
} from '@src/components/OMS/NewOrder/orderFormSecurityEligibility';
import { rfqSliceActions } from '@src/components/OMS/NewRFQ/RFQSlice';
import { openView } from '@src/components/OMS/OMSSlice';
import { OMSView } from '@src/components/OMS/OMSView';
import { modifySalesOrderThunk, resubmitSalesOrderThunk } from '@src/components/OMS/SalesOrder/SalesOrderSlice';
import { mapOrderToCustomerOrder } from '@src/components/OMS/utils';
import type { GenerateOrderDetailsRoute } from '@src/containers/Trading/Markets/OrderDetails/types';
import { useAdminUtils, useEmbeddedContext, useFeatureFlag, useOrderAccess, useRoleAuth } from '@src/hooks';
import { useNavigateToOrderDetails } from '@src/hooks/useNavigateToOrderDetails';
import { useOrderInteractions, useSubAccounts } from '@src/providers';
import { useAppStateDispatch } from '@src/providers/AppStateProvider';
import { useTradingSettings } from '@src/providers/TradingSettingsContext';
import { canResubmitPredictionClose } from '@src/utils/canResubmitPredictionClose';
import { useAssertOrderModifiable } from '@src/utils/order';
import {
  ACTION,
  IconName,
  MixpanelEvent,
  MixpanelEventProperty,
  abbreviateId,
  agGridCustomMenuItem,
  agGridMenuSeparator,
  filterByCellValueMenuItem,
  getOrdersForModify,
  getRowNodesToOperateOn,
  isOrderComplete,
  useCallbackRef,
  useDisclosure,
  useMixpanel,
  useSecuritiesContext,
  useUserContext,
  type AgGridMenuItems,
  type ExecutionReport,
  type FilterableProperty,
  type MixpanelInstance,
  type Order,
  type Security,
  type UseDisclosureReturn,
  type UseFilterBuilderOutput,
} from '@talos/kyoko';
import React, { useMemo, useState } from 'react';
import { CancelSelectedDialog, ForceCancelSelectedDialog } from '../CancelSelectedDialog';
import { useForceCancelCareOrderChildMenuItem } from '../CareOrders/MenuItems/useForceCancelCareOrderChildMenuItem';
import { useCopyCustomerOrderTextMenuItem } from '../CustomerOrders/useCopyCustomerOrderTextMenuItem';
import {
  BulkModifyDialog,
  BulkModifyGroupDialog,
  BulkModifySubAccountDialog,
  BulkModifyWarningDialog,
  BulkResumeDialog,
} from './Dialogs';
import { colIDToFilterBuilderKey } from './useOrderFilter';

export const useOrderMenu = ({
  openClause,
  filterableProperties,
  generateOrderDetailsRoute,
  layoutId,
}: {
  openClause?: UseFilterBuilderOutput['addAndOpenClause'];
  filterableProperties?: FilterableProperty<string>[];
  generateOrderDetailsRoute: GenerateOrderDetailsRoute;
  layoutId: string | undefined;
}): {
  getCustomContextMenuItems: (params: GetContextMenuItemsParams<Order>) => AgGridMenuItems;
  dialogComponents: JSX.Element[];
} => {
  const [selectedOrders, setSelectedOrders] = useState<Order[]>([]);

  const mixpanel = useMixpanel();
  const { openDetailsDrawer } = useDetailsDrawer();
  const { pause, resume, cancel, archive, unarchive } = useOrderInteractions();
  const { forceCancelOrder } = useAdminUtils();
  const { isAuthorized } = useRoleAuth();
  const { user } = useUserContext();
  const { enableGroups, confirmOrderResume, confirmOrderCancels, enableSalesWorkflow, enableCustomerBlotterColumns } =
    useTradingSettings();
  const { enableArchiveOrders, enableBulkModifyOrders } = useFeatureFlag();
  const { subAccountsEnabled } = useSubAccounts();

  const checkOrderAccess = useOrderAccess();
  const isOrderModifiable = useAssertOrderModifiable();
  const { securitiesBySymbol } = useSecuritiesContext();
  const { getForceCancelCareOrderChildOrderItem, dialog: forceCancelCareOrderChildDialog } =
    useForceCancelCareOrderChildMenuItem();

  const resumeDialog = useDisclosure();
  const cancelDialog = useDisclosure();
  const forceCancelDialog = useDisclosure();
  const modifySubAccountDialog = useDisclosure();
  const modifyGroupDialog = useDisclosure();
  const bulkModifyDialog = useDisclosure();
  const modifyWarningDialog = useDisclosure();
  const dispatch = useAppStateDispatch();

  // AG Grid retains generated menu actions across renders; these handlers must use the latest settings and providers.
  const onResume = useCallbackRef((orders: Order[]) => {
    if (confirmOrderResume) {
      resumeDialog.open();
    } else {
      orders.forEach(order => {
        mixpanel.track(MixpanelEvent.ResumeOrder, { [MixpanelEventProperty.ID]: order.OrderID });
        resume(order.OrderID);
      });
    }
  });

  const onCancel = useCallbackRef((orders: Order[]) => {
    if (confirmOrderCancels) {
      cancelDialog.open();
    } else {
      orders.forEach(order => {
        mixpanel.track(MixpanelEvent.CancelOrder, { [MixpanelEventProperty.ID]: order.OrderID });
        cancel(order.OrderID);
      });
    }
  });
  const onArchive = useCallbackRef((orders: Order[]) => orders.forEach(order => archive(order.OrderID)));
  const onUnarchive = useCallbackRef((orders: Order[]) => orders.forEach(order => unarchive(order.OrderID)));

  const forceCancelItem = (orders: Order[]) => forceCancelOrderTalosMenuItem({ orders, forceCancelDialog, mixpanel });
  const modifyWithNewOrderForm = (order: Order | ExecutionReport) => {
    dispatch(openView(OMSView.NewOrderForm));
    dispatch(orderSliceActions.modifyOrder(order));
  };
  const archiveItem = (orders: Order[]) => archiveOrderMenuItem({ orders, onArchive, mixpanel });
  const unarchiveItem = (orders: Order[]) => unarchiveOrderMenuItem({ orders, onUnarchive, mixpanel });
  const modifyItem = (orders: Order[]) =>
    modifyOrderMenuItem({
      orders,
      modifyWarningDialog,
      name: user.Name,
      modify: modifyWithNewOrderForm,
      mixpanel,
      isOrderModifiable,
      securitiesBySymbol,
    });
  const modifyCustomerItem = (orders: Order[]) =>
    modifyCustomerOrderMenuItem({
      orders,
      modifyWarningDialog,
      name: user.Name,
      modify: order => {
        const customerOrder = mapOrderToCustomerOrder(order);
        dispatch(modifySalesOrderThunk({ order: customerOrder, principalOrderID: order.OrderID }));
        dispatch(openView(OMSView.SalesOrder));
      },
      mixpanel,
      disabled: !enableSalesWorkflow,
    });
  const { isEmbedded } = useEmbeddedContext();
  const onOpenDetails = useNavigateToOrderDetails(generateOrderDetailsRoute, layoutId);
  const openDetailsItem = (orders: Order[]) => openDetailsMenuItem({ orders, onOpenDetails, isEmbedded });
  const openAnalyticsItem = (orders: Order[]) => openAnalyticsMenuItem({ orders, openDetailsDrawer, mixpanel });
  const pauseMenuItem = (orders: Order[]) => pauseOrderMenuItem({ orders, onPause: pause, mixpanel });
  const resubmitMenuItem = (orders: Order[]) =>
    resubmitOrderMenuItem({
      orders,
      resubmitOrder: order => {
        if (resubmitsViaSalesOrderForm(order)) {
          const customerOrder = mapOrderToCustomerOrder(order);
          dispatch(resubmitSalesOrderThunk(customerOrder));
          dispatch(openView(OMSView.SalesOrder));
        } else {
          dispatch(openView(OMSView.NewOrderForm));
          dispatch(orderSliceActions.resubmitOrder({ order }));
        }
      },
      resubmitRFQ: order => {
        dispatch(openView(OMSView.RFQForm));
        dispatch(rfqSliceActions.resubmitRFQ(order));
      },
      mixpanel,
      securitiesBySymbol,
    });
  const resubmitRemainingMenuItem = (orders: Order[]) => {
    const resubmitWithNewOrderForm = (order: Order, useRemaining: boolean) => {
      dispatch(openView(OMSView.NewOrderForm));
      dispatch(orderSliceActions.resubmitOrder({ order, useRemaining }));
    };

    return resubmitRemainingOrdersMenuItem({
      orders,
      openResubmitOrder: resubmitWithNewOrderForm,
      mixpanel,
      securitiesBySymbol,
    });
  };

  const modifyItems = (orders: Order[]) =>
    enableBulkModifyOrders
      ? modifyOrderMenuItems({ orders, mixpanel, name: user.Name, bulkModifyDialog, modifyWarningDialog })
      : null;

  const getMenuItems = (orders: Order[]) => {
    // We only show customer order related menu items if all selected orders are customer orders
    const selectionIsOnlyCustomerOrders = orders.every(o => o.parentIsCustomerOrder);

    const openMenuItems = compact([openAnalyticsItem(orders), openDetailsItem(orders)]);
    const remainingMenuItems: (AgGridMenuItems[number] | null)[] = [];

    // Only add the Customer Order Execution Copy Details menu item if customer columns are enabled
    if (enableCustomerBlotterColumns) {
      remainingMenuItems.push(copyCustomerOrderExecutionTextMenuItem(orders.at(0)));
    }

    const allowedToModifyOrders = every(orders, order => checkOrderAccess(order));
    if (allowedToModifyOrders) {
      const modifyOrderActions = compact([
        // The items without a permission check opens a dialog where we check the permission
        isAuthorized(ACTION.PAUSE_ORDER) && pauseMenuItem(orders),
        isAuthorized(ACTION.SUBMIT_ORDER) && modifyItem(orders),
        isAuthorized(ACTION.VIEW_SALES_ORDER_FORM) && modifyCustomerItem(orders),
        modifyItems(orders),
        cancelOrderMenuItem({ orders, onCancel }),
        isAuthorized(ACTION.FORCE_CANCEL_ORDER) && forceCancelItem(orders),
        getForceCancelCareOrderChildOrderItem(orders),
        isAuthorized(ACTION.RESUME_ORDER) && resumeOrderMenuItem({ orders, onResume }),
        isAuthorized(ACTION.SUBMIT_ORDER) &&
          modifySubAccountMenuItem({ orders, modifySubAccountDialog, subAccountsEnabled, mixpanel }),
        isAuthorized(ACTION.SUBMIT_ORDER) && modifyGroupMenuItem({ orders, modifyGroupDialog, enableGroups, mixpanel }),
        isAuthorized(ACTION.ARCHIVE_ORDER) && enableArchiveOrders && archiveItem(orders),
        isAuthorized(ACTION.ARCHIVE_ORDER) && enableArchiveOrders && unarchiveItem(orders),
      ]);
      remainingMenuItems.push(...modifyOrderActions);
      if (isAuthorized(ACTION.SUBMIT_ORDER)) {
        const resubmitItems = compact([resubmitMenuItem(orders), resubmitRemainingMenuItem(orders)]);

        if (resubmitItems.length > 0) {
          const previousMenuItem = compact(remainingMenuItems).at(-1);
          if (previousMenuItem != null && previousMenuItem !== agGridMenuSeparator) {
            remainingMenuItems.push(agGridMenuSeparator);
          }
          remainingMenuItems.push(...resubmitItems);
        }
      }
    }

    const filteredRemaining = selectionIsOnlyCustomerOrders
      ? remainingMenuItems
      : remainingMenuItems.filter(item => typeof item === 'string' || item?.name !== 'Modify Customer Order');

    const visibleRemaining = compact(filteredRemaining);
    return compact([
      ...openMenuItems,
      ...(openMenuItems.length > 0 && visibleRemaining.length > 0 ? [agGridMenuSeparator] : []),
      ...visibleRemaining,
    ]);
  };

  const copyCustomerOrderExecutionTextMenuItem = useCopyCustomerOrderTextMenuItem();

  // AG Grid retains this event callback; it must keep a stable identity while reading the latest menu state.
  const getCustomContextMenuItems = useCallbackRef((params: GetContextMenuItemsParams<Order>) => {
    const newSelectedOrders = compact(getRowNodesToOperateOn(params).map(node => node.data));
    setSelectedOrders(newSelectedOrders);
    const menuItems: AgGridMenuItems = [];

    if (filterableProperties != null && openClause != null) {
      menuItems.push(
        ...filterByCellValueMenuItem({
          params,
          filterableProperties,
          openClause,
          colIDToFilterBuilderKey,
          mixpanel,
        })
      );
    }

    menuItems.push(...getMenuItems(newSelectedOrders));

    return menuItems;
  });

  const ordersForResume = useMemo(() => getOrdersForResume(selectedOrders), [selectedOrders]);
  const ordersForCancel = useMemo(() => getOrdersForCancel(selectedOrders), [selectedOrders]);
  const ordersForModify = useMemo(() => getOrdersForModify(selectedOrders), [selectedOrders]);
  const ordersForSubAccountModify = useMemo(
    () => getOrdersForSubAccountModify(selectedOrders, subAccountsEnabled),
    [selectedOrders, subAccountsEnabled]
  );
  const ordersForGroupModify = useMemo(
    () => getOrdersForGroupModify(selectedOrders, enableGroups),
    [selectedOrders, enableGroups]
  );

  return {
    getCustomContextMenuItems,
    dialogComponents: [
      <BulkResumeDialog key="resume" selectedOrders={ordersForResume} resumeDialog={resumeDialog} />,
      <CancelSelectedDialog
        key="cancel"
        selectedItems={ordersForCancel}
        cancelSelectedDialog={cancelDialog}
        onConfirm={orders =>
          orders.forEach(order => {
            mixpanel.track(MixpanelEvent.CancelOrder, { [MixpanelEventProperty.ID]: order.OrderID });
            cancel(order.OrderID);
          })
        }
      />,
      <ForceCancelSelectedDialog
        key="force-cancel"
        selectedItems={selectedOrders}
        forceCancelSelectedDialog={forceCancelDialog}
        onConfirm={orders => orders.forEach(order => forceCancelOrder(order.OrderID))}
      />,
      <BulkModifyWarningDialog
        key="modify-warn"
        {...modifyWarningDialog}
        onConfirm={() => bulkModifyDialog.open()}
        selectedOrders={ordersForModify}
      />,
      <BulkModifySubAccountDialog
        key="modify-subaccount"
        selectedOrders={ordersForSubAccountModify}
        {...modifySubAccountDialog}
      />,
      <BulkModifyGroupDialog key="modify-group" selectedOrders={ordersForGroupModify} {...modifyGroupDialog} />,
      ...(enableBulkModifyOrders
        ? [
            <BulkModifyDialog
              key="bulk-modify"
              selectedOrders={ordersForModify}
              closeOnClickOutside={false}
              {...bulkModifyDialog}
            />,
          ]
        : []),
      <React.Fragment key="force-cancel-care-order-child-order">{forceCancelCareOrderChildDialog}</React.Fragment>,
    ],
  };
};

interface OrderMenuItemProps {
  orders: Order[];
  mixpanel: MixpanelInstance;
}

const pauseOrderMenuItem = ({ orders, onPause, mixpanel }: OrderMenuItemProps & { onPause: (id: string) => any }) => {
  if (!orders) {
    return null;
  }

  const ordersForPause = orders.filter(order => order.isPausable);
  if (ordersForPause.length === 0) {
    return null;
  }

  if (ordersForPause.length === 1) {
    const order = ordersForPause[0];
    const abbreviatedOrderId = abbreviateId(order.OrderID);
    return agGridCustomMenuItem<Order>({
      name: `Pause Order (#${abbreviatedOrderId})`,
      action: () => {
        mixpanel.track(MixpanelEvent.PauseOrder);
        onPause(order.OrderID);
      },
      iconName: IconName.Pause,
      label: (
        <>
          Pause Order (#<b>{abbreviatedOrderId}</b>)
        </>
      ),
    });
  }
  return {
    name: `Pause ${ordersForPause.length} Orders`,
    action: () => {
      mixpanel.track(MixpanelEvent.PauseOrder);
      ordersForPause.forEach(order => onPause(order.OrderID));
    },
    icon: `<i class="ag-icon ${IconName.Pause}"/>`,
  };
};

const getOrdersForCancel = (orders: Order[]) => orders.filter(order => !isOrderComplete(order.OrdStatus));

const cancelOrderMenuItem = ({ orders, onCancel }: { orders: Order[]; onCancel: (orders: Order[]) => void }) => {
  const ordersForCancel = getOrdersForCancel(orders);
  if (ordersForCancel.length === 0) {
    return null;
  }

  const name = ordersForCancel.length === 1 ? 'Cancel Order' : `Cancel ${ordersForCancel.length} Orders`;
  return {
    name,
    action: () => onCancel(ordersForCancel),
    icon: `<i class="ag-icon ${IconName.Trash}"/>`,
    ordersForCancel,
  };
};
const forceCancelOrderTalosMenuItem = ({
  orders,
  forceCancelDialog,
}: OrderMenuItemProps & {
  forceCancelDialog: UseDisclosureReturn;
}) => {
  if (orders.length === 0) {
    return null;
  }

  const name = orders.length === 1 ? 'Force Cancel Order (Talos)' : `Force Cancel ${orders.length} Orders (Talos)`;
  return {
    name,
    action: () => forceCancelDialog.open(),
    icon: `<i class="ag-icon ${IconName.Trash}"/>`,
  };
};

const modifyOrderMenuItem = ({
  orders,
  modifyWarningDialog,
  name,
  modify,
  mixpanel,
  isOrderModifiable,
  securitiesBySymbol,
}: OrderMenuItemProps & {
  modifyWarningDialog: UseDisclosureReturn;
  name: string;
  modify: (order: Order) => any;
  isOrderModifiable: (order: Order) => boolean;
  securitiesBySymbol: Map<string, Security>;
}) => {
  const ordersForModify = getOrdersForModify(orders);
  if (ordersForModify.length !== 1) {
    return null;
  }

  const selectedOrder = ordersForModify[0];
  const formEligibility = getOrderFormEligibilityForSymbol(selectedOrder.Symbol, securitiesBySymbol);

  return {
    name: 'Modify Order',
    action: () => {
      mixpanel.track(MixpanelEvent.ModifyOrder);
      selectedOrder.User === name ? modify(selectedOrder) : modifyWarningDialog.open();
    },
    icon: `<i class="ag-icon ${IconName.Pencil}"/>`,
    // if parentOrderId is set it means it's a customer order, and we don't support modifying customer hedge orders atm
    disabled: !isOrderModifiable(selectedOrder) || !formEligibility.allowed,
    tooltip: formEligibility.reason,
  };
};

const modifyCustomerOrderMenuItem = ({
  orders,
  modifyWarningDialog,
  name,
  modify,
  mixpanel,
  disabled,
}: OrderMenuItemProps & {
  modifyWarningDialog: UseDisclosureReturn;
  name: string;
  modify: (order: Order) => any;
  disabled?: boolean;
}) => {
  const ordersForModify = getOrdersForModify(orders);
  if (ordersForModify.length !== 1) {
    return null;
  }

  return {
    name: 'Modify Customer Order',
    action: () => {
      mixpanel.track(MixpanelEvent.ModifySalesOrder);
      const selectedOrder = ordersForModify[0];
      modify(selectedOrder);
    },
    icon: `<i class="ag-icon ${IconName.Pencil}"/>`,
    disabled: !ordersForModify[0].isCustomerOrderModifiable || disabled,
  };
};

const modifyOrderMenuItems = ({
  orders,
  mixpanel,
  name,
  bulkModifyDialog,
  modifyWarningDialog,
}: OrderMenuItemProps & {
  name: string;
  bulkModifyDialog: UseDisclosureReturn;
  modifyWarningDialog: UseDisclosureReturn;
}) => {
  const ordersForModify = getOrdersForModify(orders);
  if (ordersForModify.length < 2) {
    return null;
  }

  return {
    name: `Modify End Time`,
    action: () => {
      mixpanel.track(MixpanelEvent.ModifyOrders);
      ordersForModify.some(order => order.User !== name) ? modifyWarningDialog.open() : bulkModifyDialog.open();
    },
    icon: `<i class="ag-icon ${IconName.Pencil}"/>`,
  };
};

interface OpenOrderMenuItemProps {
  orders: Pick<Order, 'OrderID'>[];
  text?: string;
  onOpenDetails: (orderID: string) => void;
  isEmbedded: boolean;
}

export const openDetailsMenuItem = ({ orders, text, onOpenDetails, isEmbedded }: OpenOrderMenuItemProps) => {
  if (!orders || orders.length > 1 || isEmbedded) {
    return null;
  }
  const order = orders[0];
  return {
    name: text ?? `Open details`,
    action: () => {
      onOpenDetails(order.OrderID);
    },
    icon: `<i class="ag-icon ${IconName.Deepdive}"/>`,
  };
};

const openAnalyticsMenuItem = ({
  orders,
  openDetailsDrawer,
  mixpanel,
}: OrderMenuItemProps & { openDetailsDrawer: (props: { entity: Order }) => void }) => {
  if (!orders || orders.length > 1) {
    return null;
  }
  const order = orders[0];
  return {
    name: `Open analytics`,
    action: () => {
      mixpanel.track(MixpanelEvent.OpenOrderAnalytics);
      openDetailsDrawer({ entity: order });
    },
    icon: `<i class="ag-icon ${IconName.ChartLine}"/>`,
  };
};

const getOrdersForResume = (orders: Order[]) =>
  orders.filter(order => !isOrderComplete(order.OrdStatus) && order.isPaused);

const resumeOrderMenuItem = ({ orders, onResume }: { orders: Order[]; onResume: (orders: Order[]) => void }) => {
  if (!orders) {
    return null;
  }

  const ordersForResume = getOrdersForResume(orders);
  if (ordersForResume.length === 0) {
    return null;
  }

  if (ordersForResume.length === 1) {
    const order = ordersForResume[0];
    const abbreviatedOrderId = abbreviateId(order.OrderID);
    return agGridCustomMenuItem<Order>({
      name: `Resume Order (#${abbreviatedOrderId})`,
      action: () => {
        onResume([order]);
      },
      iconName: IconName.PlayCircle,
      label: (
        <>
          Resume Order (#<b>{abbreviatedOrderId}</b>)
        </>
      ),
    });
  }
  return {
    name: `Resume ${ordersForResume.length} Orders`,
    action: () => {
      onResume(ordersForResume);
    },
    icon: `<i class="ag-icon ${IconName.PlayCircle}"/>`,
    ordersForResume,
  };
};

const getOrdersForSubAccountModify = (orders: Order[], subAccountsEnabled: boolean) =>
  orders.filter(order => isOrderComplete(order.OrdStatus) && (order.Allocation != null || subAccountsEnabled));

const modifySubAccountMenuItem = ({
  orders,
  modifySubAccountDialog,
  subAccountsEnabled = false,
  mixpanel,
}: OrderMenuItemProps & {
  modifySubAccountDialog: UseDisclosureReturn;
  subAccountsEnabled: boolean;
}) => {
  const ordersForSubAccountModify = getOrdersForSubAccountModify(orders, subAccountsEnabled);
  if (ordersForSubAccountModify.length === 0) {
    return null;
  }
  if (ordersForSubAccountModify.length === 1) {
    const order = ordersForSubAccountModify[0];
    const name = order.Allocation != null ? 'Modify Sub Account(s)' : 'Modify Sub Account';
    return {
      name,
      action: () => {
        modifySubAccountDialog.open();
        mixpanel.track(MixpanelEvent.ModifySubaccount);
      },
      icon: `<i class="ag-icon ${IconName.Pencil}"/>`,
      ordersForSubAccountModify,
    };
  }

  return {
    name: `Modify Sub Account(s) for ${ordersForSubAccountModify.length} Orders`,
    action: () => {
      modifySubAccountDialog.open();
      mixpanel.track(MixpanelEvent.ModifySubaccount);
    },
    icon: `<i class="ag-icon ${IconName.Pencil}"/>`,
    ordersForSubAccountModify,
  };
};

const archiveOrderMenuItem = ({
  orders,
  onArchive,
  mixpanel,
}: OrderMenuItemProps & { onArchive: (orders: Order[]) => void }) => {
  const ordersForArchive = orders.filter(order => order.isArchivable);
  if (ordersForArchive.length === 0) {
    return null;
  }
  const actionName = ordersForArchive.length === 1 ? 'Archive Order' : `Archive (${ordersForArchive.length}) Orders`;
  return {
    name: actionName,
    action: () => {
      mixpanel.track(MixpanelEvent.ArchiveOrder);
      onArchive(ordersForArchive);
    },
    icon: `<i class="ag-icon ${IconName.Archive}"/>`,
  };
};

const unarchiveOrderMenuItem = ({
  orders,
  onUnarchive,
  mixpanel,
}: OrderMenuItemProps & { onUnarchive: (orders: Order[]) => void }) => {
  const ordersForUnarchive = orders.filter(order => order.isUnArchivable);
  if (ordersForUnarchive.length === 0) {
    return null;
  }
  const actionName =
    ordersForUnarchive.length === 1 ? 'Unarchive Order' : `Unarchive (${ordersForUnarchive.length}) Orders`;
  return {
    name: actionName,
    action: () => {
      mixpanel.track(MixpanelEvent.UnarchiveOrder);
      onUnarchive(ordersForUnarchive);
    },
    icon: `<i class="ag-icon ${IconName.Archive}"/>`,
  };
};

const getOrdersForGroupModify = (orders: Order[], enableGroups: boolean) =>
  orders.filter(order => isOrderComplete(order.OrdStatus) && enableGroups);

const modifyGroupMenuItem = ({
  orders,
  modifyGroupDialog,
  enableGroups,
  mixpanel,
}: OrderMenuItemProps & {
  modifyGroupDialog: UseDisclosureReturn;
  enableGroups: boolean;
}) => {
  const ordersForGroupModify = getOrdersForGroupModify(orders, enableGroups);
  if (ordersForGroupModify.length === 0) {
    return null;
  }
  if (ordersForGroupModify.length === 1) {
    return {
      name: 'Modify Group',
      action: () => {
        mixpanel.track(MixpanelEvent.ModifyOrderGroup);
        modifyGroupDialog.open();
      },
      icon: `<i class="ag-icon ${IconName.Pencil}"/>`,
      ordersForGroupModify,
    };
  }
  return null; // no bulk order group modification
};

const resubmitOrderMenuItem = ({
  orders,
  resubmitOrder,
  resubmitRFQ,
  mixpanel,
  securitiesBySymbol,
}: OrderMenuItemProps & {
  resubmitOrder: (order: Order, useRemaining: boolean) => any;
  resubmitRFQ: (order: Order) => any;
  securitiesBySymbol: Map<string, Security>;
}) => {
  if (orders.length !== 1) {
    return null;
  }

  // No bulk resubmit
  const order = orders[0];
  if (order.QuoteID) {
    return {
      name: 'Resubmit RFQ',
      action: () => {
        resubmitRFQ(order);
      },
      icon: `<i class="ag-icon ${IconName.Repeat}"/>`,
    };
  }
  if (!canResubmitPredictionClose(order)) {
    return null;
  }
  const formEligibility = getOrderFormEligibilityForResubmit(order, securitiesBySymbol);
  return {
    name: 'Resubmit Order',
    action: () => {
      mixpanel.track(MixpanelEvent.OpenResubmitOrder);
      resubmitOrder(order, false);
    },
    icon: `<i class="ag-icon ${IconName.Repeat}"/>`,
    disabled: !formEligibility.allowed,
    tooltip: formEligibility.reason,
  };
};

const resubmitRemainingOrdersMenuItem = ({
  orders,
  openResubmitOrder,
  mixpanel,
  securitiesBySymbol,
}: OrderMenuItemProps & {
  openResubmitOrder: (order: Order, useRemaining: boolean) => any;
  securitiesBySymbol: Map<string, Security>;
}) => {
  if (orders.length !== 1) {
    return null;
  } // Never show when multi selecting
  const ordersForResubmit = orders.filter(
    order => isOrderComplete(order.OrdStatus) && canResubmitPredictionClose(order)
  );
  if (ordersForResubmit.length !== 1) {
    return null;
  } // No bulk resubmit

  const order = ordersForResubmit[0];
  const { OrderQty, CumQty } = order;
  const remaining = Big(OrderQty).minus(CumQty || '0');
  if (remaining.gt(0)) {
    const formEligibility = getOrderFormEligibilityForSymbol(order.Symbol, securitiesBySymbol);
    return {
      name: 'Resubmit Remaining',
      action: () => {
        mixpanel.track(MixpanelEvent.OpenResubmitRemaining);
        openResubmitOrder(order, true);
      },
      icon: `<i class="ag-icon ${IconName.RepeatOnce}"/>`,
      disabled: !formEligibility.allowed,
      tooltip: formEligibility.reason,
    };
  }
  return null;
};
